import { useState } from "react";
import { Activity, Container, FileText, Package } from "lucide-react";
import { DataCard, DetailRow } from "@/components/ui/data-card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ZoomableImage } from "@/components/ui/ZoomableImage";
import { TrailerBolPreviewDialog } from "@/components/trailers/TrailerBolPreviewDialog";
import {
  LoadStatusCell,
  TrailerAssociatedOrdersCell,
  TrailerDwellCell,
  TrailerLocationCell,
  TrailerRecentEventCell,
  TrailerStatusCell,
} from "@/components/trailers/TrailerRowCells";

interface TrailerCardProps {
  /* Row shape comes straight from the `list_*_page` RPCs, which return JSON. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  trailer: any;
  typeLabel: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allLocations?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (trailer: any) => void;
  /** Signed URL for the trailer photo, rendered as a header thumbnail. */
  imageUrl?: string | null;
  /** Retained for call-site compatibility; both values render the same layout. */
  variant?: "default" | "yard";
}

/** Mobile/tablet representation of a trailer row (Trailers + Yard View). */
export function TrailerCard({ trailer, typeLabel, allLocations, onClick, imageUrl }: TrailerCardProps) {
  const [zoomOpen, setZoomOpen] = useState(false);
  const docCount = trailer.doc_count ?? 0;
  return (
    <>
      <DataCard onClick={() => onClick?.(trailer)}>
        <DataCard.Header className="flex-wrap">
          {imageUrl ? (
            <span
              role="button"
              tabIndex={0}
              aria-label={`View photo of ${trailer.trailer_id}`}
              onClick={(e) => {
                e.stopPropagation();
                setZoomOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.stopPropagation();
                  e.preventDefault();
                  setZoomOpen(true);
                }
              }}
            >
              <img
                src={imageUrl}
                alt={`Trailer ${trailer.trailer_id}`}
                loading="lazy"
                className="h-10 w-10 rounded object-cover"
              />
            </span>
          ) : (
            <div className="h-10 w-10 rounded bg-muted flex items-center justify-center shrink-0">
              <Container className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          <span className="font-medium truncate">{trailer.trailer_id}</span>
          <TrailerStatusCell status={trailer.current_location_status} sinceIso={trailer.latest_event?.occurred_at} />
          <TrailerDwellCell trailer={trailer} />
          {trailer.scac ? (
            <span className="text-xs text-muted-foreground">{trailer.scac}</span>
          ) : null}
        </DataCard.Header>

        <div className="divide-y divide-border">
          <DetailRow label="Trailer type" value={typeLabel} />
          <DetailRow label="SCAC" value={trailer.scac ?? "—"} />
          <DetailRow
            label="Location"
            value={<TrailerLocationCell trailer={trailer} allLocations={allLocations} />}
          />
          <DetailRow label="Load status" value={<LoadStatusCell loadStatus={trailer.load_status} />} />
        </div>

        <div className="flex flex-col gap-2">
          <DataCard.Tile
            icon={Activity}
            label="Most recent event"
            value="Most recent event"
            trailing={<TrailerRecentEventCell event={trailer.latest_event} align="right" />}
          />
          <DataCard.Tile
            icon={Package}
            label="Associated orders"
            value="Associated orders"
            trailing={
              <span onClick={(e) => e.stopPropagation()}>
                <TrailerAssociatedOrdersCell orders={trailer.associated_orders ?? []} inline />
              </span>
            }
          />
        <DataCard.Tile
          icon={FileText}
          label="Documents"
          value="Documents"
          trailing={
            docCount > 0 ? (
              <span className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                <TrailerBolPreviewDialog
                  trailerId={trailer.id}
                  trailerLabel={trailer.trailer_id}
                  docCount={docCount}
                />
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">None</span>
            )
          }
        />
        </div>
      </DataCard>

      {imageUrl ? (
        <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>Trailer {trailer.trailer_id}</DialogTitle>
            </DialogHeader>
            <ZoomableImage
              src={imageUrl}
              openUrl={imageUrl}
              fileName={`trailer-${trailer.trailer_id}.jpg`}
              alt={`Trailer ${trailer.trailer_id}`}
              className="max-h-[70vh]"
            />
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}